/**
 * Cliente API de solicitudes de recurso (Sprint 17 S17-12 tab Solicitudes).
 *
 * El tesorero solo VE solicitudes en este sprint — el flujo de
 * aprobación/rechazo lo hace la comisión desde /admin/validaciones
 * (S17-20, Fase 4).
 */
import { apiFetch } from './client';
import type {
  SolicitudRecursoConDetalle,
  SolicitudRecursoCreate,
  SolicitudRecursoRead,
} from './types';

export function listSolicitudesConDetalle(
  filtros: { estado?: string; activas?: boolean } = {},
): Promise<SolicitudRecursoConDetalle[]> {
  const params = new URLSearchParams();
  if (filtros.estado) params.set('estado', filtros.estado);
  if (filtros.activas) params.set('activas', 'true');
  const qs = params.toString();
  return apiFetch<SolicitudRecursoConDetalle[]>(
    `/solicitudes-recurso/con-detalle${qs ? `?${qs}` : ''}`,
  );
}

/** Sprint 17 S17-16 — el vecino crea una solicitud de recurso. */
export function crearSolicitudRecursoVecino(
  data: SolicitudRecursoCreate,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>('/me/solicitudes-recurso', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Motivos válidos al reportar un problema con un recurso (Sprint 18 P8). */
export type MotivoReporte = 'perdida' | 'robo' | 'deterioro' | 'falla_fabrica';

export interface ReportarProblemaPayload {
  motivo: MotivoReporte;
  motivo_texto?: string | null;
  /** Opcional: si no se indica, el backend usa el residente_actual. */
  residente_id?: string | null;
}

/** Sprint 18 P8 — el vecino reporta un problema con un recurso físico.
 * Marca el recurso como `reportado` y crea la solicitud de reposición. */
export function reportarProblemaRecurso(
  recursoId: string,
  data: ReportarProblemaPayload,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/me/recursos/${recursoId}/reportar`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/**
 * Sprint 17 S17-20 — comisión aprueba una solicitud. El backend
 * bifurca por motivo (falla_fabrica salta directo a esperando_asignacion;
 * el resto crea IngresoVario en por_cobrar).
 */
export function aprobarSolicitudRecurso(
  solicitudId: string,
  motivoAprobacion?: string | null,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/aprobar`,
    {
      method: 'POST',
      body: JSON.stringify({ motivo_aprobacion: motivoAprobacion ?? null }),
    },
  );
}

/** Sprint 17 S17-20 — comisión rechaza una solicitud con motivo. */
export function rechazarSolicitudRecurso(
  solicitudId: string,
  motivoRechazo: string,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/rechazar`,
    {
      method: 'POST',
      body: JSON.stringify({ motivo_rechazo: motivoRechazo }),
    },
  );
}

/**
 * C5 — cancela manualmente una solicitud en `esperando_cobro` (D-S17-5):
 * anula el IngresoVario asociado y marca la solicitud `cancelada_manual`.
 * El motivo es obligatorio. 409 si la solicitud no está en esperando_cobro.
 */
export function cancelarSolicitudManual(
  solicitudId: string,
  motivoCancelacion: string,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/cancelar-manual`,
    {
      method: 'POST',
      body: JSON.stringify({ motivo_cancelacion: motivoCancelacion }),
    },
  );
}

/**
 * RP-05 (2026-06-18): cancela una entrega en `esperando_retiro` (el vecino
 * ya no la retira, error de cobro, etc.). Si fue pagada, `destinoDinero`
 * decide el destino: 'saldo_a_favor' devuelve el cobro como crédito de la
 * casa (anula el IngresoVario); 'reembolso' no toca el cobro (el tesorero
 * registra el gasto de devolución aparte). 409 si no está en
 * `esperando_retiro` o si el cobro no es reversible.
 */
export function cancelarEntregaSolicitud(
  solicitudId: string,
  motivoCancelacion: string,
  destinoDinero: 'saldo_a_favor' | 'reembolso',
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/cancelar-entrega`,
    {
      method: 'POST',
      body: JSON.stringify({
        motivo_cancelacion: motivoCancelacion,
        destino_dinero: destinoDinero,
      }),
    },
  );
}

/**
 * Entrega física (2026-06): el tesorero confirma que el vecino retiró el
 * recurso. Saca el item de stock, crea el Recurso entregado y cierra la
 * solicitud. Solo procede desde `esperando_retiro`. 422 si no hay stock,
 * 409 si la solicitud no está en `esperando_retiro`.
 */
export function marcarEntregado(
  solicitudId: string,
  stockId: string,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/asignar-desde-stock`,
    { method: 'POST', body: JSON.stringify({ stock_id: stockId }) },
  );
}

/**
 * 2026-06: cobra una solicitud en `esperando_cobro` usando el saldo a favor
 * de la casa (sin pago nuevo). Reasigna crédito → venta del recurso y deja
 * la solicitud `esperando_retiro`. 422 si el saldo a favor no cubre el
 * costo total, 409 si la solicitud no está en `esperando_cobro`.
 */
export function pagarRecursoConSaldoAFavor(
  solicitudId: string,
): Promise<SolicitudRecursoRead> {
  return apiFetch<SolicitudRecursoRead>(
    `/solicitudes-recurso/${solicitudId}/pagar-con-saldo-a-favor`,
    { method: 'POST' },
  );
}
